import {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import type { CheckoutProps } from '../types';
import { getExpressCheckoutMerchantConfig } from '../../../utils/session/getExpressCheckoutMerchantConfig';
import { AppContext } from '../../contextStore/AppContext';
import { NotificationContext } from '../../contextStore/NotificationContext';
import CancelCallbackContext from '../../checkout-v2/context/cancel-callback-context';

type UseWalletManagerArgs = {
  formattedPaymentMethods: CheckoutProps['formattedPaymentMethods'];
};

export const useWalletManager = ({
  formattedPaymentMethods,
}: UseWalletManagerArgs) => {
  const {
    customerId,
    checkoutSessionId,
    amount,
    selectedPaymentMethodId,
    setSelectedPaymentMethodId,
    authorizeCard,
    paymentDescription,
    statementDescriptorSuffix,
    merchantTransactionId,
    setData,
    vendorPlatformKey,
    originalCheckoutSessionResponse,
  } = useContext(AppContext);
  const [
    shouldLoadSpinnerUntilReady,
    setShouldLoadSpinnerUntilReady,
  ] = useState<boolean>(true);

  const { isApplePayButtonEnabled, isGooglePayButtonEnabled } =
    getExpressCheckoutMerchantConfig(
      originalCheckoutSessionResponse,
    );

  const { notify, closeNotification } = useContext(
    NotificationContext,
  );

  // TODO: move useContext(CancelCallbackContext) back to PaymentWithWalletRouteContainer.tsx
  // hint: loading react component as declarative vs function is messing up the loading order of components for primary-secondary container types
  const { onCancel } = useContext(CancelCallbackContext);

  useEffect(() => {
    if (!selectedPaymentMethodId) {
      const selectedCard =
        formattedPaymentMethods?.length === 1
          ? formattedPaymentMethods[0]
          : formattedPaymentMethods.find((c) => c.isDefault);
      if (selectedCard) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setSelectedPaymentMethodId(selectedCard.id);
      }
    }
  }, [formattedPaymentMethods, selectedPaymentMethodId]);

  useEffect(() => {
    if (
      (isApplePayButtonEnabled || isGooglePayButtonEnabled) &&
      shouldLoadSpinnerUntilReady
    ) {
      setData({ overlayLoaderConfig: { show: true } });
    } else {
      setData({ overlayLoaderConfig: { show: false } });
    }
  }, [
    isApplePayButtonEnabled,
    isGooglePayButtonEnabled,
    shouldLoadSpinnerUntilReady,
  ]);

  const onLoadComplete = useCallback(() => {
    setShouldLoadSpinnerUntilReady(false);
  }, []);

  return {
    customerId,
    checkoutSessionId,
    amount,
    selectedPaymentMethodId,
    setSelectedPaymentMethodId,
    authorizeCard,
    paymentDescription,
    statementDescriptorSuffix,
    merchantTransactionId,
    setData,
    vendorPlatformKey,
    originalCheckoutSessionResponse,
    notify,
    closeNotification,
    onCancel,
    onLoadComplete,
  };
};
